// Controls.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useVideo } from "../../context/VideoContext";
import { useSubtitleSettings } from "../../context/SubtitleSettingsContext";
import { useShadowTechnique } from "../../context/ShadowTechniqueContext";
import { formatTime } from "../../utils/formatTime";
import "./Controls.css";
import CustomSpeedControl from "./CustomSpeedControl";
import { useNotification } from "../../context/NotificationContext";

const Controls = ({
  showControls = true,
  onComparisonClick,
  onCollectionSearchClick,
  onSaveCollectionClick,
  onNewVideoSelect,
  onUserInteracting,
}) => {
  const {
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackRate,
    togglePlay,
    toggleMute,
    handleSeek,
    handleVolumeChange,
    toggleFullscreen,
    setShowSettings,
    controlsAutoHide,
    changePlaybackRate,
    setControlsAutoHide,
    setShowDelayBox,
    showDelayBox,
    videoRef,
    subtitle1Loaded,
    subtitle2Loaded,
    showSubtitles,
    subtitle1Json,
    subtitle2Json,
  } = useVideo();
  const { showInfo } = useNotification();

  const { setShowSettingsPanel } = useSubtitleSettings();
  const { showProcessBox, setShowProcessBox } = useShadowTechnique();

  // Stateهای محلی
  const [showVolume, setShowVolume] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);

  const volumeControlRef = useRef(null);
  const progressBarRef = useRef(null);
  const moreMenuRef = useRef(null);
  const volumeSliderRectRef = useRef(null);
  const volumeTimeoutRef = useRef(null);

  // تشخیص موبایل
  const isMobileDevice = useCallback(() => {
    return (
      /Android|iPhone|iPad|iPod|webOS/i.test(navigator.userAgent) ||
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0
    );
  }, []);

  // تشخیص تغییر سایز پنجره
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // بستن منوی بیشتر با کلیک بیرون
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
    };
  }, []);

  // ============== توابع کمکی ==============
  const convertTimeToSeconds = (timeString) => {
    const parts = timeString.split(/[:,.]/);
    if (parts.length >= 3) {
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseInt(parts[2], 10);
      const milliseconds = parts.length > 3 ? parseInt(parts[3], 10) : 0;
      return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
    }
    return 0;
  };

  const calculateTimeFromPosition = useCallback(
    (clientX) => {
      if (!progressBarRef.current) return 0;
      const rect = progressBarRef.current.getBoundingClientRect();
      const pos = (clientX - rect.left) / rect.width;
      return Math.max(0, Math.min(1, pos)) * duration;
    },
    [duration],
  );

  const calculateVolumeFromPosition = useCallback(
    (clientY, rect) => {
      if (!rect) return volume;
      const pos = 1 - (clientY - rect.top) / rect.height;
      return Math.max(0, Math.min(1, pos));
    },
    [volume],
  );

  // ============== هندلرهای موس (دسکتاپ) ==============
  const handleProgressMouseMove = (e) => {
    const time = calculateTimeFromPosition(e.clientX);
    setHoverTime(time);
  };

  const handleProgressMouseLeave = () => {
    if (!isDraggingProgress) setHoverTime(null);
  };

  const handleProgressMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingProgress(true);
    setIsScrubbing(true);

    const newTime = calculateTimeFromPosition(e.clientX);
    handleSeek(newTime);

    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      const time = calculateTimeFromPosition(moveEvent.clientX);
      handleSeek(time);
      setHoverTime(time);
    };

    const handleMouseUp = () => {
      setIsDraggingProgress(false);
      setIsScrubbing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("scrubbing");
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.body.classList.add("scrubbing");
  };

  const handleVolumeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingVolume(true);

    const rect = e.currentTarget.getBoundingClientRect();
    volumeSliderRectRef.current = rect;
    handleVolumeChange(calculateVolumeFromPosition(e.clientY, rect));

    const handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      if (!volumeSliderRectRef.current) return;
      handleVolumeChange(
        calculateVolumeFromPosition(
          moveEvent.clientY,
          volumeSliderRectRef.current,
        ),
      );
    };

    const handleMouseUp = () => {
      setIsDraggingVolume(false);
      volumeSliderRectRef.current = null;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // ============== هندلرهای تاچ (موبایل) ==============
  const handleProgressTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    e.stopPropagation();

    setIsDraggingProgress(true);
    setIsScrubbing(true);

    const touch = e.touches[0];
    const time = calculateTimeFromPosition(touch.clientX);
    handleSeek(time);
    setHoverTime(time);
  };

  const handleProgressTouchMove = (e) => {
    if (!isDraggingProgress || e.touches.length !== 1) return;
    e.preventDefault();

    const touch = e.touches[0];
    const time = calculateTimeFromPosition(touch.clientX);
    handleSeek(time);
    setHoverTime(time);
  };

  const handleProgressTouchEnd = () => {
    setIsDraggingProgress(false);
    setIsScrubbing(false);
    setHoverTime(null);
  };

  const handleVolumeTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    e.stopPropagation();

    setIsDraggingVolume(true);
    const rect = e.currentTarget.getBoundingClientRect();
    volumeSliderRectRef.current = rect;
    handleVolumeChange(calculateVolumeFromPosition(e.touches[0].clientY, rect));
  };

  const handleVolumeTouchMove = (e) => {
    if (!isDraggingVolume || e.touches.length !== 1) return;
    e.preventDefault();

    if (!volumeSliderRectRef.current) return;
    handleVolumeChange(
      calculateVolumeFromPosition(
        e.touches[0].clientY,
        volumeSliderRectRef.current,
      ),
    );
  };

  const handleVolumeTouchEnd = () => {
    setIsDraggingVolume(false);
    volumeSliderRectRef.current = null;
  };

  // 👈 افکت برای ثبت event listenerهای تاچ روی کل داکیومنت
  useEffect(() => {
    if (isDraggingProgress) {
      const handleMove = (e) => handleProgressTouchMove(e);
      const handleEnd = () => handleProgressTouchEnd();

      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);

      return () => {
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
      };
    }
  }, [isDraggingProgress]);

  useEffect(() => {
    if (isDraggingVolume) {
      const handleMove = (e) => handleVolumeTouchMove(e);
      const handleEnd = () => handleVolumeTouchEnd();

      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);

      return () => {
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
      };
    }
  }, [isDraggingVolume]);

  useEffect(() => {
    // 👈 وقتی هر کدوم از اینا فعال باشه، نوار رو نگه دار
    const isInteracting =
      isDraggingProgress || isDraggingVolume || isScrubbing || showVolume; // 👈 اینو اضافه کن

    if (onUserInteracting) {
      onUserInteracting(isInteracting);
    }
  }, [
    isDraggingProgress,
    isDraggingVolume,
    isScrubbing,
    showVolume,
    onUserInteracting,
  ]);
  // ============== باقی توابع ==============
  const goToNextSubtitle = () => {
    if (!videoRef.current || !duration) return;
    const subtitles = subtitle1Loaded ? subtitle1Json : subtitle2Json;
    if (!subtitles || subtitles.length === 0) return;

    const adjustedTime = currentTime + 0.11;
    let currentSubtitleIndex = -1;
    for (let i = 0; i < subtitles.length; i++) {
      const sub = subtitles[i];
      const startTime = convertTimeToSeconds(sub.startTime);
      const endTime = convertTimeToSeconds(sub.endTime);
      if (adjustedTime >= startTime && adjustedTime <= endTime) {
        currentSubtitleIndex = i;
        break;
      }
    }

    let targetTime;
    if (
      currentSubtitleIndex !== -1 &&
      currentSubtitleIndex < subtitles.length - 1
    ) {
      const nextSubtitle = subtitles[currentSubtitleIndex + 1];
      targetTime = convertTimeToSeconds(nextSubtitle.startTime) - 0.1;
    } else if (currentSubtitleIndex === subtitles.length - 1) {
      targetTime = duration;
    } else {
      let nextSubtitle = null;
      for (let i = 0; i < subtitles.length; i++) {
        const startTime = convertTimeToSeconds(subtitles[i].startTime);
        if (startTime > adjustedTime) {
          nextSubtitle = subtitles[i];
          break;
        }
      }
      targetTime = nextSubtitle
        ? convertTimeToSeconds(nextSubtitle.startTime) - 0.1
        : duration;
    }

    targetTime = Math.min(duration, Math.max(0, targetTime));
    handleSeek(targetTime);
  };

  const goToPreviousSubtitle = () => {
    if (!videoRef.current || !duration) return;
    const subtitles = subtitle1Loaded ? subtitle1Json : subtitle2Json;
    if (!subtitles || subtitles.length === 0) return;

    const adjustedTime = currentTime - 0.1;
    let currentSubtitleIndex = -1;
    for (let i = 0; i < subtitles.length; i++) {
      const sub = subtitles[i];
      const startTime = convertTimeToSeconds(sub.startTime);
      const endTime = convertTimeToSeconds(sub.endTime);
      if (adjustedTime >= startTime && adjustedTime <= endTime) {
        currentSubtitleIndex = i;
        break;
      }
    }

    let targetTime;
    if (currentSubtitleIndex > 0) {
      const previousSubtitle = subtitles[currentSubtitleIndex - 1];
      targetTime = convertTimeToSeconds(previousSubtitle.startTime) - 0.1;
    } else if (currentSubtitleIndex === 0) {
      targetTime = 0;
    } else {
      let previousSubtitle = null;
      for (let i = subtitles.length - 1; i >= 0; i--) {
        const startTime = convertTimeToSeconds(subtitles[i].startTime);
        if (startTime < adjustedTime) {
          previousSubtitle = subtitles[i];
          break;
        }
      }
      targetTime = previousSubtitle
        ? convertTimeToSeconds(previousSubtitle.startTime) - 0.1
        : 0;
    }

    targetTime = Math.max(0, targetTime);
    handleSeek(targetTime);
  };

  const handleProgressClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    handleSeek(pos * duration);
  };

  const handleVolumeSliderClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    handleVolumeChange(calculateVolumeFromPosition(e.clientY, rect));
  };

  const handleVolumeMouseEnter = () => {
    if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
    setShowVolume(true);
  };

  const handleVolumeMouseLeave = (e) => {
    if (isDraggingVolume) return;
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !(relatedTarget instanceof Node)) {
      volumeTimeoutRef.current = setTimeout(() => {
        if (!isDraggingVolume) setShowVolume(false);
      }, 300);
      return;
    }
    const volumeSliderContainer = volumeControlRef.current?.querySelector(
      ".volume-slider-container",
    );
    if (
      !volumeSliderContainer ||
      !volumeSliderContainer.contains(relatedTarget)
    ) {
      volumeTimeoutRef.current = setTimeout(() => {
        if (!isDraggingVolume) setShowVolume(false);
      }, 300);
    }
  };

  const handleVolumeSliderMouseLeave = () => {
    if (isDraggingVolume) return;
    volumeTimeoutRef.current = setTimeout(() => {
      const volumeControl = volumeControlRef.current;
      const isHoveringControl = volumeControl?.matches(":hover");
      const isHoveringSlider = volumeControl
        ?.querySelector(".volume-slider-container")
        ?.matches(":hover");
      if (!isHoveringControl && !isHoveringSlider && !isDraggingVolume) {
        setShowVolume(false);
      }
    }, 300);
  };

  const handleControlClick = (e, action) => {
    e.stopPropagation();
    switch (action) {
      case "play":
        togglePlay();
        break;
      case "mute":
        toggleMute();
        break;
      case "fullscreen":
        toggleFullscreen();
        break;
      case "subtitles":
        setShowSettingsPanel(true);
        break;
      case "prevSubtitle":
        goToPreviousSubtitle();
        break;
      case "nextSubtitle":
        goToNextSubtitle();
        break;
      default:
        break;
    }
  };

  // ============== منوی بیشتر ==============
  const moreMenuItems = [
    {
      icon: "fa-thumbtack",
      label: controlsAutoHide ? "پین کردن نوار" : "لغو پین",
      onClick: () => {
        setControlsAutoHide(!controlsAutoHide);
        setShowMoreMenu(false);
      },
      active: !controlsAutoHide,
      highlight: true,
    },
    { divider: true },
    {
      icon: "fa-moon",
      label: "تکنیک سایه",
      onClick: () => {
        setShowProcessBox(!showProcessBox);
        setShowMoreMenu(false);
      },
      active: showProcessBox,
    },
    {
      icon: "fa-clock",
      label: "تاخیر زیرنویس",
      onClick: () => {
        setShowDelayBox(!showDelayBox);
        setShowMoreMenu(false);
      },
      active: showDelayBox,
    },
    {
      icon: "fa-balance-scale",
      label: "مقایسه و انتخاب زیرنویس",
      onClick: () => {
        if (onComparisonClick) onComparisonClick();
        setShowMoreMenu(false);
      },
    },
    {
      icon: "fa-save",
      label: "ذخیره زیرنویس‌های فیلم",
      onClick: () => {
        if (!subtitle1Loaded || !subtitle2Loaded) {
          showInfo("برای ذخیره باید دو زیرنویس هماهنگ انتخاب شده باشند", {
            duration: 3500,
            type: "warning",
          });
        } else {
          if (onSaveCollectionClick) onSaveCollectionClick();
          setShowMoreMenu(false);
        }
      },
    },
    {
      icon: "fa-search",
      label: "جستجوی دو زیرنویس",
      onClick: () => {
        if (onCollectionSearchClick) onCollectionSearchClick();
        setShowMoreMenu(false);
      },
    },
    {
      icon: "fa-folder-open",
      label: "انتخاب فیلم جدید",
      onClick: () => {
        if (onNewVideoSelect) onNewVideoSelect();
        setShowMoreMenu(false);
      },
    },
    { divider: true },
    {
      icon: "fa-cog",
      label: "تنظیمات",
      onClick: () => {
        setShowSettings(true);
        setShowMoreMenu(false);
      },
    },
  ];

  // ============== JSX ==============
  return (
    <div
      className={`controls ${showControls ? "visible" : "hidden"}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* نوار پیشرفت - حالا هم موس هم تاچ */}
      <div
        className="progress-bar"
        ref={progressBarRef}
        onClick={handleProgressClick}
        onMouseDown={handleProgressMouseDown}
        onMouseMove={handleProgressMouseMove}
        onMouseLeave={handleProgressMouseLeave}
        onTouchStart={handleProgressTouchStart}
        onTouchMove={handleProgressTouchMove}
        onTouchEnd={handleProgressTouchEnd}
      >
        <div
          className="progress-filled"
          style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
        />
        <div
          className="progress-thumb"
          style={{ left: `${(currentTime / duration) * 100 || 0}%` }}
        />
      </div>

      {/* تودیپ زمان */}
      {hoverTime !== null && (
        <div
          className="time-tooltip"
          style={{
            left: `${(hoverTime / duration) * 100}%`,
          }}
        >
          {formatTime(hoverTime)}
        </div>
      )}

      <div className="controls-bottom">
        {/* سمت چپ */}
        <div className="controls-left">
          <div className="more-menu-container" ref={moreMenuRef}>
            <button
              className={`control-btn more-btn ${showMoreMenu ? "active" : ""}`}
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              title="بیشتر"
            >
              <i className="fas fa-ellipsis-v"></i>
            </button>

            {showMoreMenu && (
              <div className="more-menu">
                {moreMenuItems.map((item, index) => {
                  if (item.divider) {
                    return (
                      <div key={`divider-${index}`} className="menu-divider" />
                    );
                  }
                  return (
                    <button
                      key={item.label}
                      onClick={item.onClick}
                      disabled={item.disabled}
                      className={`${item.active ? "active-item" : ""} ${item.highlight ? "highlight-item" : ""}`}
                    >
                      <i className={`fas ${item.icon}`}></i>
                      <span>{item.label}</span>
                      {item.active && <span className="active-badge"></span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            className="control-btn fullscreen-btn"
            onClick={(e) => handleControlClick(e, "fullscreen")}
            title="تمام صفحه"
          >
            <i className="fas fa-expand"></i>
          </button>

          {/* کنترل سرعت با پشتیبانی تاچ */}
          <div className="speed-control">
            <CustomSpeedControl
              playbackRate={playbackRate}
              onChange={changePlaybackRate}
            />
          </div>
        </div>

        {/* مرکز */}
        <div className="controls-center">
          <button
            className="control-btn subtitle-nav-btn next-sub"
            onClick={(e) => handleControlClick(e, "nextSubtitle")}
            title="زیرنویس بعدی"
            disabled={!subtitle1Loaded && !subtitle2Loaded}
          >
            <span className="subtitle-nav-icon">{`‹S`}</span>
          </button>

          <button
            className="control-btn play-btn"
            onClick={(e) => handleControlClick(e, "play")}
          >
            <i className={`fas fa-${isPlaying ? "pause" : "play"}`}></i>
          </button>

          <button
            className="control-btn subtitle-nav-btn prev-sub"
            onClick={(e) => handleControlClick(e, "prevSubtitle")}
            title="زیرنویس قبلی"
            disabled={!subtitle1Loaded && !subtitle2Loaded}
          >
            <span className="subtitle-nav-icon">{`S›`}</span>
          </button>
        </div>

        {/* سمت راست */}
        <div className="controls-right">
          <button
            className={`control-btn subtitle-btn ${(subtitle1Loaded || subtitle2Loaded) && showSubtitles ? "active" : ""}`}
            onClick={(e) => handleControlClick(e, "subtitles")}
            title="تنظیمات زیرنویس"
          >
            <i className="fas fa-closed-captioning"></i>
          </button>

          {/* ولوم با پشتیبانی تاچ */}
          <div
            ref={volumeControlRef}
            className="volume-control"
            onMouseEnter={handleVolumeMouseEnter}
            onMouseLeave={handleVolumeMouseLeave}
          >
            <button
              className="control-btn volume-btn"
              onClick={(e) => handleControlClick(e, "mute")}
              title={isMuted ? "صدا دارد" : "بی صدا"}
            >
              <i
                className={`fas fa-volume-${isMuted || volume === 0 ? "mute" : volume > 0.5 ? "up" : "down"}`}
              ></i>
              <span className="volume-percentage-badge">
                {Math.round(volume * 100)}%
              </span>
            </button>

            {showVolume && (
              <div
                className={`volume-slider-container ${isDraggingVolume ? "dragging" : ""}`}
                onMouseEnter={handleVolumeMouseEnter}
                onMouseLeave={handleVolumeSliderMouseLeave}
              >
                <div
                  className="volume-slider"
                  onClick={handleVolumeSliderClick}
                  onMouseDown={handleVolumeMouseDown}
                  onTouchStart={handleVolumeTouchStart}
                  onTouchMove={handleVolumeTouchMove}
                  onTouchEnd={handleVolumeTouchEnd}
                >
                  <div
                    className="volume-level"
                    style={{ height: `${volume * 100}%` }}
                  />
                  <div
                    className="volume-thumb"
                    style={{ bottom: `${volume * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="time-display" onClick={(e) => e.stopPropagation()}>
            <span>{formatTime(currentTime)}</span>
            <span> / </span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Controls;
